
const SECTION_HELP_TEXT = {
  'Recent Activity': 'Review the latest activity happening across your account.',
  'Admins': 'Manage the people who can access and operate this account.',
  'Authentication': 'Create and manage login, registration, and email confirmation settings for your app.',
  'Billing': 'Store subscription details and subscription status for this account.',
  'Brands': 'Manage your business profile, branding, and support details.',
  'Forms': 'Create forms for collecting structured submissions from your users.',
  'Help': 'Write and manage help articles for your users or internal team.',
  'Support': 'Create a support ticket and we\'ll contact you to solve your issue.',
  'Surveys': 'Create surveys and guided question flows for feedback and responses.'
};

function getSectionHelpText(title) {
  return SECTION_HELP_TEXT[title] || '';
}

function appendHelpTooltip(parent, title) {
  const helpText = getSectionHelpText(title);
  if (!helpText) return;

  const tooltip = document.createElement('span');
  tooltip.className = 'title-help';

  const trigger = document.createElement('button');
  trigger.type = 'button';
  trigger.className = 'title-help-trigger';
  trigger.setAttribute('aria-label', `What does ${title} do?`);
  trigger.textContent = '?';

  const bubble = document.createElement('span');
  bubble.className = 'title-help-bubble';
  bubble.textContent = helpText;

  tooltip.appendChild(trigger);
  tooltip.appendChild(bubble);
  parent.appendChild(tooltip);
}

function setSharedFieldAttributes(field, props) {
  field.name = props.name || '';
  field.placeholder = '';
  field.setAttribute('onfocus', "Form.labelHandler(this, 'focus')");
  field.setAttribute('onblur', "Form.labelHandler(this, 'blur')");
  const isRequired = props.required !== false && props.required !== 'false';
  if (isRequired) {
    field.setAttribute('required', '');
  }
}

function buildFloatingLabelField(fieldTag, props = {}) {
  const fragment = document.createDocumentFragment();
  const label = document.createElement('label');
  label.className = 'untouched';

  const title = document.createElement('span');
  title.textContent = props.label || '';
  label.appendChild(title);

  const field = document.createElement(fieldTag);
  setSharedFieldAttributes(field, props);

  if (fieldTag === 'textarea' && props.rows) {
    field.rows = props.rows;
  }

  label.appendChild(field);
  fragment.appendChild(label);
  return fragment;
}

export class FloatingLabelInput {
  static props = ['label', 'name', 'required', 'type'];

  static render(props) {
    const fragment = buildFloatingLabelField('input', props);
    const input = fragment.querySelector('input');
    if (props.type) {
      input.type = props.type;
    }
    return fragment;
  }
}

export class FloatingLabelTextarea {
  static props = ['label', 'name', 'rows', 'required'];

  static render(props) {
    return buildFloatingLabelField('textarea', props);
  }
}

export class FloatingLabelSelect {
  static props = ['label', 'name', 'options', 'required', 'value'];

  static render(props) {
    const fragment = document.createDocumentFragment();
    const label = document.createElement('label');
    label.className = 'touched';

    const title = document.createElement('span');
    title.textContent = props.label || '';
    label.appendChild(title);

    const select = document.createElement('select');
    select.name = props.name || '';
    const isRequired = props.required !== false && props.required !== 'false';
    if (isRequired) {
      select.setAttribute('required', '');
    }

    const options = Array.isArray(props.options) ? props.options : [];
    const selectedValue = props.value == null ? '' : String(props.value);

    options.forEach(option => {
      const normalized = typeof option === 'string'
        ? { value: option, label: option }
        : option || {};
      const optionEl = document.createElement('option');
      optionEl.value = normalized.value ?? normalized.label ?? '';
      optionEl.textContent = normalized.label ?? normalized.value ?? '';
      if (String(optionEl.value) === selectedValue) {
        optionEl.selected = true;
      }
      select.appendChild(optionEl);
    });

    label.appendChild(select);
    fragment.appendChild(label);
    return fragment;
  }
}

export class Breadcrumbs {
  static props = [
    'title',
    'href',
    'nameHref',
    'compact',
    'nameStyle',
    'nameSeparatorStyle',
    'suffix',
    'suffixStyle',
    'suffixSeparatorStyle'
  ];

  static render(props) {
    const fragment = document.createDocumentFragment();
    const row = document.createElement('div');
    const isCompact = props.compact === true || props.compact === 'true';
    row.className = isCompact ? 'horizontal-flex compact-breadcrumbs' : 'horizontal-flex';
    const h1 = document.createElement('h1');
    h1.className = 'flex1';

    const titleLink = document.createElement('a');
    titleLink.className = 'breadcrumb-title';
    titleLink.href = props.href || '';
    titleLink.textContent = props.title || '';
    h1.appendChild(titleLink);

    const nameSeparator = document.createElement('span');
    nameSeparator.className = 'px-1 breadcrumb-separator';
    if (props.nameSeparatorStyle) nameSeparator.style.cssText = props.nameSeparatorStyle;
    nameSeparator.textContent = '>';
    h1.appendChild(nameSeparator);

    const nameLink = document.createElement('a');
    nameLink.id = 'name';
    nameLink.className = 'breadcrumb-name';
    nameLink.href = props.nameHref || '';
    if (props.nameStyle) nameLink.style.cssText = props.nameStyle;
    const loading = document.createElement('span');
    loading.className = 'loading-ellipse';
    nameLink.appendChild(loading);
    h1.appendChild(nameLink);

    const suffixSeparator = document.createElement('span');
    suffixSeparator.className = 'px-1 breadcrumb-separator';
    if (props.suffixSeparatorStyle) suffixSeparator.style.cssText = props.suffixSeparatorStyle;
    suffixSeparator.textContent = '>';
    h1.appendChild(suffixSeparator);

    const suffix = document.createElement('span');
    suffix.className = 'breadcrumb-suffix';
    if (props.suffixStyle) suffix.style.cssText = props.suffixStyle;
    suffix.textContent = props.suffix || '';
    h1.appendChild(suffix);

    row.appendChild(h1);
    fragment.appendChild(row);
    return fragment;
  }
}

export class PageTitle {
  static props = ['title'];

  static render(props) {
    const fragment = document.createDocumentFragment();
    const h1 = document.createElement('h1');
    h1.className = 'page-title';
    h1.textContent = props.title || '';
    appendHelpTooltip(h1, props.title || '');
    fragment.appendChild(h1);
    return fragment;
  }
}

function localizedHref(href) {
  if (!href?.startsWith('/')) return href || '';
  const languageCode = globalThis.Translations?.langCode;
  return languageCode ? `/${languageCode}${href}` : href;
}

function createLineIcon(name, className = '') {
  const icons = {
    rocket: '<path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/><path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/><path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/><path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/>',
    mail: '<rect width="20" height="16" x="2" y="4" rx="2.5"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>',
    link: '<path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>',
    url: '<circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/><path d="M2 12h20"/>',
    users: '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>',
    team: '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>',
    code: '<polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/><line x1="14" y1="4" x2="10" y2="20"/>',
    integrate: '<path d="m12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83Z"/><path d="m22 17.65-9.17 4.16a2 2 0 0 1-1.66 0L2 17.65"/><path d="m22 12.65-9.17 4.16a2 2 0 0 1-1.66 0L2 12.65"/>',
    form: '<rect width="16" height="20" x="4" y="2" rx="2"/><path d="M8 7h8"/><path d="M8 11h8"/><path d="M8 15h5"/>',
    chart: '<path d="M3 3v18h18"/><path d="m19 9-5 5-4-4-3 3"/>',
    settings: '<path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/>',
    globe: '<circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/><path d="M2 12h20"/>',
    check: '<circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/>'
  };
  const wrapper = document.createElement('span');
  wrapper.className = className;
  wrapper.innerHTML = `<svg viewBox="0 0 24 24" aria-hidden="true">${icons[name] || icons.check}</svg>`;
  return wrapper;
}

function appendFieldHelp(parent, helpText, label) {
  if (!helpText) return;
  const container = document.createElement('span');
  container.className = 'create-field-help';

  const trigger = document.createElement('button');
  trigger.type = 'button';
  trigger.className = 'create-field-help-trigger';
  trigger.setAttribute('aria-label', `Help for ${label}`);
  trigger.textContent = '?';

  const bubble = document.createElement('span');
  bubble.className = 'create-field-help-bubble';
  bubble.setAttribute('role', 'tooltip');
  bubble.textContent = helpText;

  container.append(trigger, bubble);
  parent.appendChild(container);
}

function createFieldShell(props) {
  const field = document.createElement('div');
  field.className = 'create-field';
  const heading = document.createElement('div');
  heading.className = 'create-field-heading';
  const label = document.createElement('label');
  const id = `create-${props.name || 'field'}`;
  label.htmlFor = id;
  label.textContent = props.label || '';
  heading.appendChild(label);
  appendFieldHelp(heading, props.help, props.label || 'Field');
  field.appendChild(heading);
  return { field, id };
}

function setCreateFieldAttributes(control, props, id) {
  control.id = id;
  control.name = props.name || '';
  control.placeholder = props.placeholder || '';
  const isRequired = props.required !== false && props.required !== 'false';
  if (isRequired) control.required = true;
}

function appendCreateFieldHint(field, hint) {
  if (!hint) return;
  const description = document.createElement('p');
  description.className = 'create-field-hint';
  description.textContent = hint;
  field.appendChild(description);
}

export class CreatePageHeader {
  static props = ['title', 'href', 'description', 'compact'];

  static render(props) {
    const fragment = document.createDocumentFragment();
    const header = document.createElement('header');
    const isCompact = props.compact === true || props.compact === 'true';
    header.className = isCompact
      ? 'create-page-header create-page-header-compact compact-breadcrumbs'
      : 'create-page-header';
    const h1 = document.createElement('h1');
    const link = document.createElement('a');
    link.className = 'breadcrumb-title';
    link.href = localizedHref(props.href);
    link.textContent = props.title || '';
    const separator = document.createElement('span');
    separator.className = 'breadcrumb-separator';
    separator.textContent = '>';
    const suffix = document.createElement('strong');
    suffix.className = 'breadcrumb-suffix';
    suffix.textContent = 'Create';
    h1.append(link, separator, suffix);
    const description = document.createElement('p');
    description.textContent = props.description || '';
    header.append(h1, description);
    fragment.appendChild(header);
    return fragment;
  }
}

export class CreateFieldInput {
  static props = ['label', 'name', 'type', 'placeholder', 'hint', 'required', 'help', 'value'];

  static render(props) {
    const fragment = document.createDocumentFragment();
    const { field, id } = createFieldShell(props);
    const input = document.createElement('input');
    setCreateFieldAttributes(input, props, id);
    input.type = props.type || 'text';
    input.value = props.value == null ? '' : String(props.value);
    field.appendChild(input);
    appendCreateFieldHint(field, props.hint);
    fragment.appendChild(field);
    return fragment;
  }
}

export class CreateFieldTextarea {
  static props = ['label', 'name', 'placeholder', 'hint', 'required', 'help', 'rows'];

  static render(props) {
    const fragment = document.createDocumentFragment();
    const { field, id } = createFieldShell(props);
    const textarea = document.createElement('textarea');
    setCreateFieldAttributes(textarea, props, id);
    textarea.rows = Number(props.rows) || 4;
    field.appendChild(textarea);
    appendCreateFieldHint(field, props.hint);
    fragment.appendChild(field);
    return fragment;
  }
}

export class CreateFieldSelect {
  static props = ['label', 'name', 'options', 'hint', 'required', 'help', 'value'];

  static render(props) {
    const fragment = document.createDocumentFragment();
    const { field, id } = createFieldShell(props);
    const select = document.createElement('select');
    setCreateFieldAttributes(select, props, id);
    const selectedValue = props.value == null ? '' : String(props.value);
    (Array.isArray(props.options) ? props.options : []).forEach(option => {
      const normalized = typeof option === 'string' ? { value: option, label: option } : option || {};
      const optionEl = document.createElement('option');
      optionEl.value = normalized.value ?? normalized.label ?? '';
      optionEl.textContent = normalized.label ?? normalized.value ?? '';
      optionEl.selected = String(optionEl.value) === selectedValue;
      select.appendChild(optionEl);
    });
    field.appendChild(select);
    appendCreateFieldHint(field, props.hint);
    fragment.appendChild(field);
    return fragment;
  }
}

export class CreateCheckboxField {
  static props = ['label', 'name', 'hint', 'checked'];

  static render(props) {
    const fragment = document.createDocumentFragment();
    const field = document.createElement('div');
    field.className = 'create-checkbox-field';
    const label = document.createElement('label');
    const input = document.createElement('input');
    input.type = 'checkbox';
    input.name = props.name || '';
    input.checked = props.checked === true || props.checked === 'true';
    const copy = document.createElement('span');
    const title = document.createElement('strong');
    title.textContent = props.label || '';
    const hint = document.createElement('span');
    hint.textContent = props.hint || '';
    copy.append(title, hint);
    label.append(input, copy);
    field.appendChild(label);
    fragment.appendChild(field);
    return fragment;
  }
}

export class CreateFormActions {
  static props = ['submitLabel', 'cancelHref'];

  static render(props) {
    const fragment = document.createDocumentFragment();
    const footer = document.createElement('footer');
    footer.className = 'create-form-actions';
    const submit = document.createElement('button');
    submit.type = 'submit';
    submit.className = 'create-primary-action';
    const rawLabel = props.submitLabel || 'Create';
    submit.textContent = rawLabel.startsWith('+') ? rawLabel : `+ ${rawLabel}`;
    const cancel = document.createElement('a');
    cancel.className = 'create-secondary-action';
    cancel.href = localizedHref(props.cancelHref);
    cancel.textContent = 'Cancel';
    footer.append(submit, cancel);
    fragment.appendChild(footer);
    return fragment;
  }
}

export class CreateNextSteps {
  static props = ['items'];

  static render(props) {
    const fragment = document.createDocumentFragment();
    const card = document.createElement('aside');
    card.className = 'create-next-card';
    const heading = document.createElement('div');
    heading.className = 'create-next-heading';
    heading.append(createLineIcon('rocket', 'create-next-rocket'));
    const title = document.createElement('h2');
    title.textContent = "What's next?";
    heading.appendChild(title);
    const intro = document.createElement('p');
    intro.textContent = 'After you create this, you can:';
    const list = document.createElement('ul');
    (Array.isArray(props.items) ? props.items : []).forEach(item => {
      const normalized = typeof item === 'string' ? { label: item, icon: 'check' } : item || {};
      const row = document.createElement('li');
      row.append(createLineIcon(normalized.icon || 'check', 'create-next-item-icon'));
      const label = document.createElement('span');
      label.textContent = normalized.label || '';
      row.appendChild(label);
      list.appendChild(row);
    });
    card.append(heading, intro, list);
    fragment.appendChild(card);
    return fragment;
  }
}

export class FormButton {
  static props = ['label'];

  static timl = `
    button.add-button.mt-10 {{ Props.label }}
  `;
}

export class FormCheckbox {
  static props = ['label', 'name', 'checked'];

  static render(props) {
    const fragment = document.createDocumentFragment();
    const label = document.createElement('label');
    label.className = 'touched';

    const title = document.createElement('span');
    title.textContent = props.label || '';
    label.appendChild(title);

    const input = document.createElement('input');
    input.type = 'checkbox';
    input.name = props.name || '';

    const isChecked = props.checked === true || props.checked === 'true';
    if (isChecked) {
      input.setAttribute('checked', '');
    }

    label.appendChild(input);
    fragment.appendChild(label);
    return fragment;
  }
}

export class CreateFormName {
  static timl = `
    label.untouched
      span Name
      input[name="name" required placeholder="" onfocus="Form.labelHandler(this, 'focus')" onblur="Form.labelHandler(this, 'blur')"]
  `
}

export class CreateFormEmail {
  static timl = `
    label.untouched
      span Email
      input[name="email" required placeholder="" onfocus="Form.labelHandler(this, 'focus')" onblur="Form.labelHandler(this, 'blur')"]
  `
}

export class CreateFormDescription {
  static timl = `
    label.untouched
      span Description (optional)
      textarea[name="description" placeholder="" onfocus="Form.labelHandler(this, 'focus')" onblur="Form.labelHandler(this, 'blur')"]
  `
}

export class CreateFormDomain {
  static timl = `
    label.untouched
      span Domain (optional)
      textarea[name="domain" placeholder="" onfocus="Form.labelHandler(this, 'focus')" onblur="Form.labelHandler(this, 'blur')"]
  `
}
